import { useMemo, useCallback, useEffect } from 'react';
import { useCurrentUser } from '../../../shared/auth';
import {
  useLocationFilter,
  type UseLocationFilterResult,
} from '../../../shared/data/preferred-location';
import { syncPreferredLocationSeed } from '../../../shared/data/preferred-location/preferredLocationSeed';
import { useDebouncedWithFlush } from '../../../shared/forms/useDebounced';
import { usePersistedFilters } from '../../../shared/forms/usePersistedFilters';
import type { FilterDateRange } from '../../../shared/domain';
import {
  AuditStatus,
  type SearchAuditsCommandInput,
  type AuditType,
  type DateRangeType,
} from '@amzn/gbsa-helios-typescript-client';

const DEBOUNCE_MS = 400;
const PAGE_SIZE = 15;

/**
 * Converts the filter date range into the shape expected by
 * `SearchAuditsCommandInput`. Extracted to keep the command-input
 * builder simple.
 */
function buildDateRangeInput(
  dateRange: FilterDateRange | null,
): SearchAuditsCommandInput['dateRange'] {
  if (!dateRange?.type) return undefined;
  return {
    type: dateRange.type as DateRangeType,
    startDate: dateRange.startDate ? new Date(dateRange.startDate) : undefined,
    endDate: dateRange.endDate ? new Date(dateRange.endDate) : undefined,
  };
}

/**
 * Builds the SearchAuditsCommandInput from current filter/debounced state.
 * Extracted to reduce cognitive complexity of the hook body.
 */
function buildCommandInput(
  businessUnit: string | undefined,
  filters: AuditListFilters,
  debouncedAssignees: string[],
  debouncedKeyword: string,
): SearchAuditsCommandInput {
  return {
    businessUnitId: businessUnit ?? '',
    statuses:
      filters.statuses.length > 0 ? (filters.statuses as AuditStatus[]) : undefined,
    auditTypes:
      filters.auditTypes.length > 0 ? (filters.auditTypes as AuditType[]) : undefined,
    templateIds: filters.templateIds.length > 0 ? filters.templateIds : undefined,
    assignees: debouncedAssignees.length > 0 ? debouncedAssignees : undefined,
    divisionName: filters.divisionName || undefined,
    locations: (filters.locations?.length ?? 0) > 0 ? filters.locations : undefined,
    dateRange: buildDateRangeInput(filters.dateRange),
    keyword: debouncedKeyword || undefined,
    maxResults: PAGE_SIZE,
  };
}

export interface AuditListFilters {
  statuses: string[];
  auditTypes: string[];
  templateIds: string[];
  assignees: string[];
  locations: string[];
  divisionName: string;
  keyword: string;
  dateRange: FilterDateRange | null;
}

// Default to active audits only — auditors landing on the list care
// about work they still need to act on. Completed/Expired can be
// surfaced via the Status filter chip when needed.
const DEFAULT_STATUSES: string[] = [AuditStatus.NOT_STARTED, AuditStatus.IN_PROGRESS];

export interface UseAuditListFiltersResult {
  filters: AuditListFilters;
  debouncedKeyword: string;
  setFilter: <K extends keyof Omit<AuditListFilters, 'keyword'>>(
    key: K,
    value: AuditListFilters[K],
  ) => void;
  applyFilters: (partial: Partial<Omit<AuditListFilters, 'keyword'>>) => void;
  setKeyword: (value: string) => void;
  resetFilters: () => void;
  commandInput: SearchAuditsCommandInput;
  locationFilter: UseLocationFilterResult;
}

/**
 * Manages audit list filter state.
 * Location is handled by the shared `useLocationFilter` hook.
 * Keyword and assignees are debounced via the shared `useDebouncedWithFlush`
 * hook so per-keystroke changes don't trigger API calls until the user
 * pauses. Explicit actions (filter flyout Apply, Reset) flush immediately —
 * a deliberate click should not wait out the keystroke debounce.
 *
 * @example
 * ```tsx
 * const { filters, debouncedKeyword, setFilter, setKeyword, commandInput, locationFilter } = useAuditListFilters();
 * const query = useSearchAuditsQuery(commandInput);
 * ```
 */
export function useAuditListFilters(): UseAuditListFiltersResult {
  const { businessUnit, preferredLocation } = useCurrentUser();
  const locationFilter = useLocationFilter();

  const AUDIT_DEFAULTS: AuditListFilters = {
    statuses: DEFAULT_STATUSES,
    auditTypes: [],
    templateIds: [],
    assignees: [],
    locations: [],
    divisionName: '',
    keyword: '',
    dateRange: null,
  };

  const INITIAL_OVERRIDES: Partial<AuditListFilters> = {
    ...(locationFilter.value ? { locations: [locationFilter.value] } : {}),
  };

  const {
    filters,
    setFilters: persistedSetFilters,
    resetFilters: persistedReset,
  } = usePersistedFilters<AuditListFilters>('audits', AUDIT_DEFAULTS, INITIAL_OVERRIDES);

  // Mirror the task list: keep the persisted `locations` filter in sync
  // with the user's preferred location if they change it on the profile
  // page while this list is mounted (or between visits within a session).
  // Without this, the persisted filter freezes whatever location seeded
  // it and a preference change won't take effect until the user manually
  // clears filters.
  useEffect(() => {
    syncPreferredLocationSeed('audits', preferredLocation, (locations) =>
      persistedSetFilters({ locations }),
    );
  }, [preferredLocation, persistedSetFilters]);

  const [debouncedKeyword, flushKeyword] = useDebouncedWithFlush(
    filters.keyword,
    DEBOUNCE_MS,
  );
  const [debouncedAssignees, flushAssignees] = useDebouncedWithFlush(
    filters.assignees,
    DEBOUNCE_MS,
  );

  const setFilter = useCallback(
    <K extends keyof Omit<AuditListFilters, 'keyword'>>(
      key: K,
      value: AuditListFilters[K],
    ) => {
      persistedSetFilters({ [key]: value });
    },
    [persistedSetFilters],
  );

  /** Batch update multiple filter keys in a single state transition.
   * Triggered by the explicit Apply action — assignees take effect
   * immediately rather than waiting out the keystroke debounce. */
  const applyFilters = useCallback(
    (partial: Partial<Omit<AuditListFilters, 'keyword'>>) => {
      persistedSetFilters(partial);
      // Flush assignees immediately on explicit Apply so the API call
      // doesn't wait out the keystroke debounce.
      if (partial.assignees) flushAssignees(partial.assignees);
    },
    [persistedSetFilters, flushAssignees],
  );

  const setKeyword = useCallback(
    (value: string) => {
      persistedSetFilters({ keyword: value });
    },
    [persistedSetFilters],
  );

  const resetFilters = useCallback(() => {
    persistedReset();
    // Explicit reset — clear debounced values immediately
    flushKeyword('');
    flushAssignees([]);
    locationFilter.reset();
  }, [locationFilter, flushKeyword, flushAssignees, persistedReset]);

  /** Ready-to-use input for useSearchAuditsQuery. */
  const commandInput = useMemo(
    () => buildCommandInput(businessUnit, filters, debouncedAssignees, debouncedKeyword),
    [businessUnit, filters, debouncedAssignees, debouncedKeyword],
  );

  return {
    filters,
    debouncedKeyword,
    setFilter,
    applyFilters,
    setKeyword,
    resetFilters,
    commandInput,
    locationFilter,
  };
}
